import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, TrendingUp, Save, X, Loader2, ChevronDown, ChevronUp, UserCog, Settings2, Wand2, Calculator, AlertTriangle } from "lucide-react";

const AREAS = ["caja", "cocina", "gerente_sucursal", "operaciones", "rh", "compras", "direccion", "legal", "tesoreria", "diseno", "otro"];
const UNITS = ["MXN", "%", "count", "min"];
const FREQUENCIES = ["diaria", "semanal", "mensual"];
const AREA_LABEL = {
  caja: "Caja", cocina: "Cocina", gerente_sucursal: "Gerente Sucursal",
  operaciones: "Operaciones", rh: "RH y Nominas", compras: "Compras",
  direccion: "Direccion", legal: "Legal", tesoreria: "Tesoreria",
  diseno: "Diseno", otro: "Otro",
};
const AREA_COLOR = {
  caja: "bg-blue-50 text-blue-700 border-blue-200",
  cocina: "bg-orange-50 text-orange-700 border-orange-200",
  gerente_sucursal: "bg-purple-50 text-purple-700 border-purple-200",
  operaciones: "bg-teal-50 text-teal-700 border-teal-200",
  rh: "bg-pink-50 text-pink-700 border-pink-200",
  compras: "bg-emerald-50 text-emerald-700 border-emerald-200",
  direccion: "bg-slate-100 text-slate-700 border-slate-200",
  legal: "bg-amber-50 text-amber-700 border-amber-200",
  tesoreria: "bg-blue-50 text-blue-700 border-blue-200",
  diseno: "bg-indigo-50 text-indigo-700 border-indigo-200",
  otro: "bg-slate-50 text-slate-600 border-slate-200",
};

function responsableLabel(ra) {
  if (!ra) return { text: "Sin asignacion", badge: "bg-red-50 text-red-600" };
  const scope = ra.scope;
  if (scope === "branch") return { text: `Branch manager`, badge: "bg-purple-50 text-purple-700" };
  if (scope === "corporate_dept") return { text: `Corporativo — ${ra.department || "?"}`, badge: "bg-blue-50 text-blue-700" };
  if (scope === "corporate_role") return { text: `Rol: ${ra.role || "?"}`, badge: "bg-teal-50 text-teal-700" };
  if (scope === "any_corporate_admin") return { text: "Cualquier corp admin", badge: "bg-slate-100 text-slate-600" };
  return { text: "Sin asignacion", badge: "bg-red-50 text-red-600" };
}

function KpiCard({ kpi, onEdit, onEditAssignment, onConfigure }) {
  const [open, setOpen] = useState(false);
  const resp = responsableLabel(kpi.responsible_assignment);
  const hasFormula = !!(kpi.formula_enabled && kpi.formula_template && (kpi.input_fields || []).length);
  return (
    <div className={`bg-white rounded-xl border ${kpi.active ? "border-slate-200" : "border-slate-100 opacity-60"}`}>
      <div
        onClick={() => setOpen(o => !o)}
        role="button"
        tabIndex={0}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50 transition-colors rounded-xl cursor-pointer"
      >
        <span className={`text-xs font-semibold px-2 py-1 rounded-full border ${AREA_COLOR[kpi.area] || AREA_COLOR.otro}`}>{AREA_LABEL[kpi.area] || kpi.area}</span>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 truncate">{kpi.name}</div>
          <div className="text-xs text-slate-500">Meta: {kpi.target} {kpi.unit} · {kpi.frequency} · peso {kpi.weight}%</div>
        </div>
        {hasFormula ? (
          <span className="shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1" data-testid={`kpi-formula-badge-${kpi.id}`}>
            <Calculator className="w-3 h-3" /> Con fórmula
          </span>
        ) : (
          <span className="shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700 border border-yellow-200" data-testid={`kpi-formula-badge-${kpi.id}`}>
            Sin fórmula
          </span>
        )}
        <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${resp.badge}`}>{resp.text}</span>
        {!kpi.active && <span className="text-xs text-slate-400 px-2 py-0.5 rounded-full bg-slate-100">Inactivo</span>}
        <button
          onClick={e => { e.stopPropagation(); onConfigure(kpi); }}
          className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#1A1A1A] text-white text-xs font-semibold hover:bg-black transition-colors"
          title="Configurar fórmula y contexto"
          data-testid={`kpi-configure-btn-${kpi.id}`}
        >
          <Settings2 className="w-3.5 h-3.5" /> Configurar
        </button>
        <button
          onClick={e => { e.stopPropagation(); onEditAssignment(kpi); }}
          className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700"
          title="Editar responsable"
        >
          <UserCog className="w-3.5 h-3.5" />
        </button>
        <button onClick={e => { e.stopPropagation(); onEdit(kpi); }} className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700" title="Edición rápida">
          <Pencil className="w-3.5 h-3.5" />
        </button>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </div>
      {open && (
        <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-1.5 text-sm text-slate-600">
          {kpi.description && <div className="italic text-slate-500">{kpi.description}</div>}
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div><span className="font-semibold text-slate-700">Verde:</span> {kpi.thresholds?.verde}</div>
            <div><span className="font-semibold text-yellow-600">Amarillo:</span> {kpi.thresholds?.amarillo}</div>
            <div><span className="font-semibold text-red-600">Rojo:</span> {kpi.thresholds?.rojo}</div>
          </div>
          {kpi.manual_capture === false && (
            <div className="text-xs text-teal-700 font-medium bg-teal-50 rounded px-2 py-1">Autocalculado (Wansoft)</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function OpsKpis() {
  const navigate = useNavigate();
  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterArea, setFilterArea] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assignEditing, setAssignEditing] = useState(null); // { kpi, scope, department, role }
  const [assignSaving, setAssignSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ active_only: showInactive ? "false" : "true" });
      if (filterArea) params.set("area", filterArea);
      const res = await api.get(`/ops/kpis/definitions?${params}`);
      setKpis(res.data);
    } catch {
      setKpis([]);
    } finally {
      setLoading(false);
    }
  }, [filterArea, showInactive]);

  useEffect(() => { load(); }, [load]);

  const grouped = kpis.reduce((acc, k) => {
    const area = k.area || "otro";
    if (!acc[area]) acc[area] = [];
    acc[area].push(k);
    return acc;
  }, {});

  const newKpi = () => {
    setEditing({ name: "", area: "caja", description: "", unit: "%", target: 100, frequency: "diaria", weight: 0, active: true, thresholds: { verde: ">=95", amarillo: ">=80", rojo: "<80" } });
    setIsNew(true);
  };

  const handleSave = async () => {
    if (!editing.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/ops/kpis/definitions", editing);
        toast.success("KPI creado");
      } else {
        await api.put(`/ops/kpis/definitions/${editing.id}`, {
          description: editing.description,
          target: parseFloat(editing.target),
          frequency: editing.frequency,
          weight: parseInt(editing.weight),
          thresholds: editing.thresholds,
          active: editing.active,
        });
        toast.success("KPI actualizado");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const upd = (field, val) => setEditing(p => ({ ...p, [field]: val }));
  const updThreshold = (key, val) => setEditing(p => ({ ...p, thresholds: { ...p.thresholds, [key]: val } }));

  const openAssignModal = (kpi) => {
    const ra = kpi.responsible_assignment || {};
    setAssignEditing({
      kpi,
      scope: ra.scope || "any_corporate_admin",
      department: ra.department || "",
      role: ra.role || "",
    });
  };

  const handleSaveAssignment = async () => {
    if (!assignEditing) return;
    setAssignSaving(true);
    try {
      await api.put(`/ops/kpis/definitions/${assignEditing.kpi.id}/assignment`, {
        scope: assignEditing.scope,
        department: assignEditing.department || null,
        role: assignEditing.role || null,
      });
      toast.success("Responsable actualizado");
      setAssignEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar asignacion");
    } finally {
      setAssignSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Definiciones de KPIs</h1>
          <p className="text-sm text-slate-500">Metas y umbrales para el semaforo operativo</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={filterArea} onChange={e => setFilterArea(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]">
            <option value="">Todas las areas</option>
            {AREAS.map(a => <option key={a} value={a}>{AREA_LABEL[a]}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none">
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} className="w-4 h-4 accent-[#FFDF00]" />
            Ver inactivos
          </label>
          <button onClick={newKpi} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#FFDF00] text-sm font-semibold text-slate-900 hover:bg-yellow-300 transition-colors" data-testid="new-kpi-btn">
            <Plus className="w-4 h-4" /> Nuevo KPI
          </button>
        </div>
      </div>

      {(() => {
        const noFormula = kpis.filter(k => !(k.formula_enabled && k.formula_template && (k.input_fields || []).length)).length;
        if (noFormula === 0) return null;
        return (
          <div className="flex items-start gap-3 bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3" data-testid="kpi-no-formula-banner">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-yellow-800">Hay {noFormula} KPIs sin fórmula configurada.</p>
              <p className="text-xs text-yellow-700">Los usuarios los capturan manualmente. Configura las fórmulas para que el sistema calcule por ellos.</p>
            </div>
            <button
              onClick={() => navigate("/ops/admin/kpis/wizard")}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#1A1A1A] text-white text-sm font-semibold hover:bg-black transition-colors"
              data-testid="kpi-wizard-btn"
            >
              <Wand2 className="w-4 h-4" /> Configurar fórmulas
            </button>
          </div>
        );
      })()}

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="bg-white rounded-xl border border-slate-200 h-16 animate-pulse" />)}</div>
      ) : kpis.length === 0 ? (
        <div className="text-center py-16">
          <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Sin KPIs definidos</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([area, items]) => (
            <div key={area}>
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-sm font-bold px-3 py-1 rounded-full border ${AREA_COLOR[area] || AREA_COLOR.otro}`}>{AREA_LABEL[area] || area}</span>
                <span className="text-xs text-slate-400">{items.length} KPIs</span>
              </div>
              <div className="space-y-2">
                {items.map(k => <KpiCard key={k.id} kpi={k} onEdit={k => { setEditing(k); setIsNew(false); }} onEditAssignment={openAssignModal} onConfigure={(kpi) => navigate(`/ops/admin/kpis/definitions/${kpi.id}/editar`)} />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-6 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">{isNew ? "Nuevo KPI" : "Editar KPI"}</h2>
              <button onClick={() => setEditing(null)} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Nombre</label>
                <input value={editing.name} onChange={e => upd("name", e.target.value)} disabled={!isNew} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] disabled:bg-slate-50" />
              </div>
              {isNew && (
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Area</label>
                  <select value={editing.area} onChange={e => upd("area", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]">
                    {AREAS.map(a => <option key={a} value={a}>{AREA_LABEL[a]}</option>)}
                  </select>
                </div>
              )}
              {isNew && (
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Unidad</label>
                  <select value={editing.unit} onChange={e => upd("unit", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]">
                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Meta</label>
                <input type="number" value={editing.target} onChange={e => upd("target", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Frecuencia</label>
                <select value={editing.frequency} onChange={e => upd("frequency", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]">
                  {FREQUENCIES.map(f => <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Ponderacion (%)</label>
                <input type="number" value={editing.weight} onChange={e => upd("weight", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Umbrales semaforo</label>
              <div className="grid grid-cols-3 gap-2">
                {[["verde", "text-green-700"], ["amarillo", "text-yellow-600"], ["rojo", "text-red-600"]].map(([k, cls]) => (
                  <div key={k}>
                    <label className={`block text-xs font-semibold ${cls} mb-1 capitalize`}>{k}</label>
                    <input value={editing.thresholds?.[k] || ""} onChange={e => updThreshold(k, e.target.value)} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#FFDF00] font-mono" placeholder=">=95" />
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-1">Operadores: &gt;= &lt;= &gt; &lt; ==</p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Descripcion (opcional)</label>
              <textarea value={editing.description} onChange={e => upd("description", e.target.value)} rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] resize-none" />
            </div>

            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input type="checkbox" checked={editing.active} onChange={e => upd("active", e.target.checked)} className="w-4 h-4 accent-[#FFDF00]" />
                KPI activo
              </label>
            </div>

            <div className="flex gap-3 pt-2 border-t border-slate-100">
              <button onClick={() => setEditing(null)} className="flex-1 py-3 rounded-xl border border-slate-200 font-semibold text-slate-600 hover:bg-slate-50 transition-colors">Cancelar</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#FFDF00] font-semibold text-slate-900 hover:bg-yellow-300 transition-colors disabled:opacity-50" data-testid="save-kpi-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar KPI
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal edición de asignación de responsable */}
      {assignEditing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">Editar responsable</h2>
              <button onClick={() => setAssignEditing(null)} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-slate-500">{assignEditing.kpi.name}</p>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Tipo de responsable</label>
              <select
                value={assignEditing.scope}
                onChange={e => setAssignEditing(p => ({ ...p, scope: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                data-testid="assign-scope-select"
              >
                <option value="branch">Branch manager (por sucursal)</option>
                <option value="corporate_dept">Corporativo por departamento</option>
                <option value="corporate_role">Corporativo por rol</option>
                <option value="any_corporate_admin">Cualquier corp admin</option>
              </select>
            </div>
            {assignEditing.scope === "corporate_dept" && (
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Departamento</label>
                <select
                  value={assignEditing.department}
                  onChange={e => setAssignEditing(p => ({ ...p, department: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                  data-testid="assign-dept-select"
                >
                  <option value="">Seleccionar...</option>
                  {["RH", "Compras", "Operaciones", "Dirección", "Legal", "Tesorería", "Diseño"].map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
            )}
            {assignEditing.scope === "corporate_role" && (
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Rol</label>
                <select
                  value={assignEditing.role}
                  onChange={e => setAssignEditing(p => ({ ...p, role: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                  data-testid="assign-role-select"
                >
                  <option value="">Seleccionar...</option>
                  {["treasury", "legal", "branch_manager", "corporate_admin", "hr_admin"].map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex gap-3 pt-2 border-t border-slate-100">
              <button onClick={() => setAssignEditing(null)} className="flex-1 py-2.5 rounded-xl border border-slate-200 font-semibold text-slate-600 text-sm hover:bg-slate-50 transition-colors">Cancelar</button>
              <button
                onClick={handleSaveAssignment}
                disabled={assignSaving}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#FFDF00] font-semibold text-sm text-slate-900 hover:bg-yellow-300 transition-colors disabled:opacity-50"
                data-testid="save-assignment-btn"
              >
                {assignSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
